import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { ApiCustomer } from '@vend/types'
import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../../../constants'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../../models/fulfilment'
import {
  useCustomerManager,
  useSaleManager,
} from '../../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  selectLineItem,
  selectLineItems,
  setFulfilmentDetails,
  updateLineItem,
  updateLineItems,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  markSaleCustomerContactInfoAsReviewed,
  selectOpenFulfilmentPopoverLineItemId,
  setExpandedLineItemIdWithFulfilmentTab,
  setExpandedLineItemIdWithFulfilmentTabPreservingPopover,
} from '../../../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import {
  CurrentView,
  useLineItemFulfillmentContext,
} from '../LineItemFulfillmentContext'
import {
  handleFulfillmentMethodViewNavigation,
  isContactInfoUpdated,
  trimCustomerInfo,
} from '../LineItemFulfillmentUtils'
import {
  fillEmptyAddressWithCustomerAddress,
  isAllAddressFieldsEmpty,
} from '../utils/addressHelper'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from './useFulfillmentHelper'

interface useLineItemFulfillmentReviewContactInfoFlowProps {
  onClose: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
  saleHasServiceOrder?: boolean
}

export const useLineItemFulfillmentReviewContactInfoFlow = ({
  onClose,
  saleHasServiceOrder = false,
}: useLineItemFulfillmentReviewContactInfoFlowProps) => {
  const {
    getFulfillmentAddressStatus,
    shouldFixFulfillmentAddresses,
  } = useFulfillmentHelper()
  const {
    setCurrentView,
    currentCustomer,
    originalCustomer,
    setIsLoading,
    onSelectFulfilmentType,
    fulfilmentDetails,
    setFulfilmentDetails: setContextFulfilmentDetails,
  } = useLineItemFulfillmentContext()

  const t = useT()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const lineItemId = useAppSelector(selectOpenFulfilmentPopoverLineItemId)
  const lineItem = useAppSelector(selectLineItem(lineItemId))
  const lineItems = useAppSelector(selectLineItems)
  const dispatch = useAppDispatch()
  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const handleContinueFromReviewContactInfoView = async (
    customerFulfilment?: FulfilmentDetail
  ) => {
    const lineItemFulfilmentType = lineItem?.fulfilmentType
      ? lineItem?.fulfilmentType
      : FulfilmentType.UNSPECIFIED

    const fulfilmentType = customerFulfilment?.type || lineItemFulfilmentType

    const newFulfillmentDetails = {
      ...fulfilmentDetails,
      ...fillEmptyAddressWithCustomerAddress(
        currentCustomer!,
        fulfilmentDetails.billingAddress,
        fulfilmentDetails.shippingAddress
      ),
    }

    if (currentCustomer && originalCustomer) {
      if (onSelectFulfilmentType) {
        // calling this here in case the fulfilment type is already set from fulfilments tab
        onSelectFulfilmentType(fulfilmentType)
      }
      const isUpdated = isContactInfoUpdated(currentCustomer, originalCustomer)
      if (isUpdated) {
        try {
          trimCustomerInfo(currentCustomer)
          setIsLoading(true)
          const savedCustomer = await customerManager.updateCustomer(
            currentCustomer
          )
          await saleManager.addCustomer(savedCustomer)
          customerFulfilment
            ? handleContinueFromOutOfStockLineItems(newFulfillmentDetails)
            : goToNextView()
        } catch (_) {
          ToastNotifications.negative(
            t('Unable to update customer. Try again.')
          )
        } finally {
          updateFulfilmentAddressDetails(newFulfillmentDetails)
          setIsLoading(false)
        }
      } else {
        await AddNewOrNotUpdatedCustomer(
          currentCustomer,
          newFulfillmentDetails,
          customerFulfilment
        )
      }
    } else if (currentCustomer && !originalCustomer) {
      await AddNewOrNotUpdatedCustomer(
        currentCustomer,
        newFulfillmentDetails,
        customerFulfilment
      )
    }
  }

  const AddNewOrNotUpdatedCustomer = async (
    currentCustomer: ApiCustomer,
    newFulfillmentDetails: FulfilmentDetail,
    customerFulfilment?: FulfilmentDetail
  ) => {
    setIsLoading(true)
    await saleManager.addCustomer(currentCustomer)
    updateFulfilmentAddressDetails(newFulfillmentDetails)
    setIsLoading(false)
    if (customerFulfilment) {
      handleContinueFromOutOfStockLineItems(newFulfillmentDetails)
    } else {
      goToNextView()
    }
  }

  const goToNextView = () => {
    if (isDefinedFulfilmentType(lineItem?.fulfilmentType)) {
      const isAddressEmpty =
        getFulfillmentAddressStatus(fulfilmentDetails) ===
        AddressErrorsStatus.EMPTYSHIPPING_EMPTYBILLING
      if (
        isCaptureAddressSellScreenEnabled &&
        lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
        currentCustomer &&
        (shouldFixFulfillmentAddresses(fulfilmentDetails) || isAddressEmpty)
      ) {
        if (isAddressEmpty) {
          dispatch(
            setFulfilmentDetails([
              { ...fulfilmentDetails, shouldAddNewAddress: true },
            ])
          )
          onClose()
        } else {
          setCurrentView(CurrentView.FIX_SALE_ADDRESSES)
        }
      } else onClose()
    } else {
      handleFulfillmentMethodViewNavigation(
        saleHasServiceOrder,
        onSelectFulfilmentType,
        onClose,
        setCurrentView
      )
    }

    dispatch(markSaleCustomerContactInfoAsReviewed())
  }

  const updateFulfilmentAddressDetails = (
    fulfilmentDetails: FulfilmentDetail
  ) => {
    if (
      isCaptureAddressSellScreenEnabled &&
      fulfilmentDetails.type === FulfilmentType.DELIVERY
    ) {
      // Update both Redux state and Context state
      // Update Redux state
      dispatch(setFulfilmentDetails([fulfilmentDetails]))

      // Update Context state so components reading from context get fresh data
      setContextFulfilmentDetails(fulfilmentDetails)

      saleManager.unfulfilledDelivery()

      return saleManager.updateFulfillmentDetails([fulfilmentDetails])
    }

    if (fulfilmentDetails.type === FulfilmentType.PICKUP)
      saleManager.unfulfilledPickUp()
  }

  const handleContinueFromOutOfStockLineItems = (
    fulfilmentDetails: FulfilmentDetail
  ) => {
    const shouldFixTheAddress = shouldFixFulfillmentAddresses(fulfilmentDetails)
    const isAddressEmpty =
      isAllAddressFieldsEmpty(fulfilmentDetails.shippingAddress) &&
      isAllAddressFieldsEmpty(fulfilmentDetails.billingAddress)

    if (
      isCaptureAddressSellScreenEnabled &&
      fulfilmentDetails.type === FulfilmentType.DELIVERY
    ) {
      if (shouldFixTheAddress || isAddressEmpty) {
        dispatch(
          setExpandedLineItemIdWithFulfilmentTabPreservingPopover(lineItemId)
        )
      } else {
        dispatch(setExpandedLineItemIdWithFulfilmentTab(lineItemId))
      }
      if (lineItem)
        // update line item to rerender popover with correct fulfilment type
        dispatch(
          updateLineItem({
            ...lineItem,
            fulfilmentType: fulfilmentDetails.type,
          })
        )

      // Find other line items (excluding the current one already updated
      // above) that have an explicit fulfillment type which differs from the
      // new type, so we can align them.
      const otherItemsToSync = lineItems.filter(
        item =>
          item.localId !== lineItemId &&
          isDefinedFulfilmentType(item.fulfilmentType) &&
          item.fulfilmentType !== fulfilmentDetails.type
      )
      if (otherItemsToSync.length > 0) {
        dispatch(
          updateLineItems(
            otherItemsToSync.map(item => ({
              ...item,
              fulfilmentType: fulfilmentDetails.type,
            }))
          )
        )
      }

      if (isAddressEmpty) {
        dispatch(
          setFulfilmentDetails([
            { ...fulfilmentDetails, shouldAddNewAddress: true },
          ])
        )
        onClose()
      }
    } else {
      onClose()
    }
  }

  return {
    handleContinueFromReviewContactInfoView,
    handleContinueFromOutOfStockLineItems,
  }
}
